import { useEffect, useState } from "react";
import { useSeo } from "@/hooks/useSeo";
import { useBreadcrumbs } from "@/hooks/useBreadcrumbs";
import { getPost, BLOG_POSTS } from "@/data/blog";
import { openBookingModal } from "@/components/booking-modal";

interface BlogPostPageProps {
  params: { slug: string };
}

export default function BlogPostPage({ params }: BlogPostPageProps) {
  const post = getPost(params.slug);
  const [loaded, setLoaded] = useState(false);

  useSeo({
    title: post?.metaTitle ?? "Статья — Паруса на крыше",
    description: post?.metaDescription ?? "",
    keywords: post?.keywords ?? "",
    image: post?.image,
    canonical: post ? `/blog/${post.slug}` : "/blog",
  });
  useBreadcrumbs([
    { name: "Блог", path: "/blog" },
    { name: post?.title ?? "Статья" },
  ]);

  // BlogPosting schema
  useEffect(() => {
    if (!post) return;
    const schema = {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      "headline": post.title,
      "description": post.metaDescription,
      "image": `https://parusanakryshe.ru${post.image}`,
      "datePublished": post.date,
      "dateModified": post.date,
      "author": {
        "@type": "Organization",
        "name": "Паруса на крыше",
        "url": "https://parusanakryshe.ru",
      },
      "publisher": {
        "@type": "Organization",
        "name": "Паруса на крыше",
        "logo": { "@type": "ImageObject", "url": "https://parusanakryshe.ru/logo.svg" },
      },
      "mainEntityOfPage": { "@type": "WebPage", "@id": `https://parusanakryshe.ru/blog/${post.slug}` },
    };
    let el = document.getElementById("blogpost-schema");
    if (!el) {
      el = document.createElement("script");
      el.id = "blogpost-schema";
      el.setAttribute("type", "application/ld+json");
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(schema);
    return () => { el?.remove(); };
  }, [post]);

  useEffect(() => { setTimeout(() => setLoaded(true), 100); }, []);

  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <p className="text-muted-foreground mb-6">Статья не найдена</p>
          <a href="/blog" className="border border-primary text-accent px-8 py-3 text-[10px] tracking-[0.3em] uppercase hover:bg-primary hover:text-background transition-colors">
            К блогу
          </a>
        </div>
      </div>
    );
  }

  const formatDate = (d: string) =>
    new Date(d).toLocaleDateString("ru-RU", { day: "numeric", month: "long", year: "numeric" });

  const relatedPosts = BLOG_POSTS.filter(p => p.slug !== post.slug).slice(0, 2);

  return (
    <div className="w-full bg-background">
      {/* Hero */}
      <section className="relative h-[60vh] min-h-[400px] flex items-end overflow-hidden border-b border-primary/10">
        <img src={post.image} alt={post.title} className="absolute inset-0 w-full h-full object-cover" style={{ filter: "brightness(0.25) saturate(0.5)" }} loading="eager" fetchPriority="high" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(3,13,9,1) 0%, rgba(3,13,9,0.4) 60%, transparent 100%)" }} />
        <div className="relative z-10 container mx-auto px-6 md:px-14 max-w-4xl pb-16"
          style={{ opacity: loaded ? 1 : 0, transform: loaded ? "none" : "translateY(20px)", transition: "all 0.8s ease 0.3s" }}>
          <a href="/blog" className="text-[9px] tracking-[0.3em] uppercase text-primary/60 hover:text-accent transition-colors mb-6 flex items-center gap-2">
            ← Блог
          </a>
          <div className="flex flex-wrap items-center gap-3 mb-5">
            <span className="bg-primary/20 border border-primary/40 text-accent text-[9px] tracking-[0.25em] uppercase px-4 py-2 shrink-0">{post.category}</span>
            <span className="text-muted-foreground/60 text-xs">{formatDate(post.date)}</span>
            <span className="text-muted-foreground/40 text-xs">{post.readTime} мин чтения</span>
          </div>
          <h1 className="font-serif text-3xl sm:text-4xl md:text-6xl font-light text-foreground leading-tight max-w-3xl">{post.title}</h1>
        </div>
      </section>

      {/* Content */}
      <article className="py-20 bg-background">
        <div className="container mx-auto px-6 md:px-14 max-w-3xl">
          {/* Lead */}
          <p className="text-lg text-foreground/80 font-light leading-relaxed mb-14 border-l-2 border-primary/30 pl-6 italic">
            {post.lead}
          </p>

          {/* Sections */}
          <div className="space-y-14">
            {post.sections.map((section, i) => (
              <div key={i}>
                <h2 className="font-serif text-2xl md:text-3xl font-light text-foreground mb-5 leading-snug">{section.heading}</h2>
                <p className="text-muted-foreground font-light leading-relaxed mb-5">{section.body}</p>
                {section.list && (
                  <ul className="space-y-3 mt-4">
                    {section.list.map((item, j) => (
                      <li key={j} className="flex items-start gap-3 text-sm text-muted-foreground font-light">
                        <span className="text-primary/50 shrink-0 mt-0.5">✦</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                )}
                {section.highlight && (
                  <div className="mt-6 border-l-2 border-primary/50 bg-primary/5 px-6 py-4">
                    <p className="text-sm text-accent/90 font-light leading-relaxed italic">{section.highlight}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </article>

      {/* CTA */}
      <section className="py-20 bg-card border-t border-b border-primary/10">
        <div className="container mx-auto px-6 text-center max-w-2xl">
          <div className="w-12 h-[1px] bg-primary/30 mx-auto mb-8" />
          <h2 className="font-serif text-3xl font-light text-foreground mb-6">{post.cta.text}</h2>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={post.cta.href} className="bg-primary/20 border border-primary text-accent px-10 py-4 text-[10px] tracking-[0.3em] uppercase hover:bg-primary hover:text-background transition-colors">
              Узнать подробнее →
            </a>
            <button onClick={openBookingModal} className="border border-primary/30 text-muted-foreground px-10 py-4 text-[10px] tracking-[0.3em] uppercase hover:border-primary hover:text-accent transition-colors">
              Забронировать стол
            </button>
          </div>
        </div>
      </section>

      {/* Related posts */}
      {relatedPosts.length > 0 && (
        <section className="py-20 bg-background">
          <div className="container mx-auto px-6 md:px-14 max-w-5xl">
            <div className="text-[9px] tracking-[0.4em] text-primary/50 uppercase mb-10">Читайте также</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {relatedPosts.map((p) => (
                <a
                  key={p.slug}
                  href={`/blog/${p.slug}`}
                  className="group block border border-primary/15 hover:border-primary/50 transition-colors overflow-hidden"
                >
                  <div className="relative overflow-hidden aspect-[16/7]">
                    <img src={p.image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" style={{ filter: "brightness(0.5)" }} loading="lazy" />
                  </div>
                  <div className="p-7">
                    <div className="text-[9px] tracking-widest text-primary/40 uppercase mb-2">{p.category}</div>
                    <h3 className="font-serif text-xl font-light text-foreground group-hover:text-accent transition-colors leading-snug">{p.title}</h3>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
